import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { LoggingService } from '@com-delta-idp/mnx-utils';
import { Subject, Subscription, timer } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  validateConnectionAndAirports,
  validateFromAndToAirportWithRequired,
  validateMulticityDeptDate
} from '../../../common/error-handler/custom-validator';
import { ErrorHandlerDirective } from '../../../common/error-handler/error-handler.directive';
import { ContentBook } from '../../../model/content/contentbook.model';
import { SearchSegmentData } from '../../../model/mc-response.interface';
import {
  Airports,
  Dates,
  RecentSearch
} from '../../../model/recent-search.model';
import { FormStoreDirective } from '../../../store/directive/form-store.directive';
import { FlightItemComponent } from './flightitem/flightitem.component';

@Component({
  selector: 'ngc-multicity',
  templateUrl: 'multicity.component.html'
})
export class MulticityComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChildren(FlightItemComponent) rows: QueryList<FlightItemComponent>;
  @ViewChild('addFlightButton') addFlightButton: ElementRef;

  @Input() bookContent: ContentBook;
  @Input() bookWidgetForm: UntypedFormGroup;
  @Input() errorHandler: ErrorHandlerDirective;
  @Input() formStoreDir: FormStoreDirective;
  @Input() isAdvSearchExpanded: boolean;
  @Input() isWidgetExpanded: boolean;
  @Input() recentSearch: RecentSearch;
  @Output() advancedSearchClicked: EventEmitter<any> = new EventEmitter();
  @Output() setODPairRestrictions = new EventEmitter();
  @Output() tripTypeChnagedTriggered = new EventEmitter();

  flightModified = false;
  flightRemovedLable: any;
  flights: UntypedFormArray;
  odPairRestriction: string;
  timerTabSectionSubscription: Subscription;

  private unsubscribe: Subject<void> = new Subject();

  constructor(
    private fb: UntypedFormBuilder,
    private cdf: ChangeDetectorRef,
    private loggingService: LoggingService
  ) {}

  ngOnInit() {
    // build the form model
    if (this.bookWidgetForm) {
      let fromAirportCode = '';
      let fromCity = '';
      let toAirportcode = '';
      let toCity = '';
      let connection = '';
      let departDate = '';

      if (this.formStoreDir && this.recentSearch) {
        // const flights = this.formStoreDir.getStoredValueFor('flights');
        const airports = this.recentSearch.airports as Airports[];
        const dates = this.recentSearch.dates as Dates[];

        if (airports && airports.length > 0) {
          const flightArray = [];

          airports.forEach((airport, i) => {
            fromAirportCode =
              airport && airport.fromAirportcode ? airport.fromAirportcode : '';

            fromCity = airport && airport.fromCity ? airport.fromCity : '';

            toAirportcode =
              airport && airport.toAirportcode ? airport.toAirportcode : '';

            toCity = airport && airport.toCity ? airport.toCity : '';

            connection =
              airport && airport.connectionAirport
                ? airport.connectionAirport
                : '';

            departDate =
              dates[i] && dates[i].departureDate ? dates[i].departureDate : '';

            flightArray.push(
              this.createNewFlight(
                i + 1,
                fromAirportCode,
                fromCity,
                toAirportcode,
                toCity,
                connection,
                departDate,
                airport.fromCountryCode,
                airport.toCountryCode
              )
            );
          });

          this.flights = this.fb.array(flightArray);
        } else {
          // this.flights = this.fb.array([
          //   this.createNewFlight(1, '', '', '', '', '', ''),
          //   this.createNewFlight(2, '', '', '', '', '', '')
          // ]);
          this.fillDataForMulticity(this.recentSearch);
        }
      } else {
        this.fillDataForMulticity();
      }
      this.bookWidgetForm.addControl('flights', this.flights);
    }
  }

  ngAfterViewInit() {
    this.rows.changes.pipe(takeUntil(this.unsubscribe)).subscribe(
      (change) => {
        if (this.flightModified) {
          this.flightModified = false;
          this.rows.last.fromAirportElementRef.nativeElement.focus();
        }
      },
      (err) => {
        this.loggingService.log('Caught Error: ' + err);
      }
    );
  }

  ngOnDestroy() {
    if (this.bookWidgetForm) {
      this.fillDataForOther();
      this.bookWidgetForm.removeControl('flights');
    }
    if (this.timerTabSectionSubscription) {
      this.timerTabSectionSubscription.unsubscribe();
    }
    this.unsubscribe.next();
    this.unsubscribe.complete();
    this.unsubscribe.unsubscribe();
    this.cdf.detach();
  }

  addFlight() {
    const n = this.flights.length + 1;
    let fromAirportCode = '';
    let fromCity = '';
    let toAirportcode = '';
    let toCity = '';
    let fromCountryCode = '';
    let toCountryCode = '';
    let connection = '';
    let departDate = '';
    this.flightRemovedLable = '';
    if (this.formStoreDir) {
      const flights = this.formStoreDir.getStoredValueFor('flights');

      if (this.flightModified && flights.length > n - 1) {
        fromAirportCode =
          flights[n - 1].airports && flights[n - 1].airports.fromAirportcode
            ? flights[n - 1].airports.fromAirportcode
            : '';
        fromCountryCode = flights[n - 1]?.airports?.fromCountryCode ?? '';
        toCountryCode = flights[n - 1]?.airports?.toCountryCode ?? '';
        fromCity =
          flights[n - 1].airports && flights[n - 1].airports.fromCity
            ? flights[n - 1].airports.fromCity
            : '';

        toAirportcode =
          flights[n - 1].airports && flights[n - 1].airports.toAirportcode
            ? flights[n - 1].airports.toAirportcode
            : '';

        toCity =
          flights[n - 1].airports && flights[n - 1].airports.toCity
            ? flights[n - 1].airports.toCity
            : '';

        connection = flights[n - 1].connectionAirport
          ? flights[n - 1].connectionAirport
          : '';

        if (n - 2 >= 0) {
          departDate = flights[n - 2].departureDate
            ? flights[n - 2].departureDate
            : '';
        }
      }
    }
    const prevFlightAirports = this.flights.value[n - 2].airports;
    const prevFlightToAirportcode = prevFlightAirports.toAirportcode;
    if (prevFlightToAirportcode !== '') {
      fromAirportCode = prevFlightToAirportcode;
      fromCountryCode = prevFlightAirports.toCountryCode;
      fromCity = prevFlightAirports.toCity;
    }
    const formItem = this.createNewFlight(
      n,
      fromAirportCode,
      fromCity,
      toAirportcode,
      toCity,
      connection,
      departDate,
      fromCountryCode,
      toCountryCode
    );
    this.flights.push(formItem);
    this.flightModified = true;
  }

  removeFlight() {
    this.flightRemovedLable = '';
    const n = this.flights.length - 1;
    this.flights.removeAt(n);
    if (
      this.odPairRestriction &&
      this.odPairRestriction.toString().toLowerCase() === 'true'
    ) {
      this.restrictions(-1, '', '', '');
    }
    if (this.flights.length === 2) {
      if (this.addFlightButton && this.addFlightButton.nativeElement) {
        // TODO - change this to a jasmine spy invocation
        this.addFlightButton.nativeElement.focus();
      }
    }
    this.flightRemovedLable = 'Removed Flight ' + (n + 1);
    this.cdf.detectChanges();
    // setTimeout(() => {
    //   this.flightRemovedLable = '';
    //   this.cdf.detectChanges();
    // }, 200);
    const timerMulti = timer(200);
    this.timerTabSectionSubscription = timerMulti.subscribe(
      (t) => {
        this.flightRemovedLable = '';
        this.cdf.detectChanges();
      },
      (err) => {
        this.loggingService.log('Caught Error: ' + err);
      }
    );
  }

  createNewFlight(
    n: number,
    fromAirportCode: string,
    fromCity: string,
    toAirportcode = '',
    toCity = '',
    connection = '',
    departureDate = '',
    fromCountryCode = '',
    toCountryCode = ''
  ) {
    return new UntypedFormGroup(
      {
        ['airports']: new UntypedFormGroup(
          {
            ['fromAirportcode']: new UntypedFormControl(fromAirportCode),
            ['fromCity']: new UntypedFormControl(fromCity),
            ['toAirportcode']: new UntypedFormControl(toAirportcode),
            ['toCity']: new UntypedFormControl(toCity),
            ['fromCountryCode']: new UntypedFormControl(fromCountryCode),
            ['toCountryCode']: new UntypedFormControl(toCountryCode),
            ['mpmRestriction']: new UntypedFormControl(''),
            ['milesRestriction']: new UntypedFormControl('')
          },
          validateFromAndToAirportWithRequired(
            'fromAirportcode',
            'toAirportcode'
          )
        ),
        ['departureDate']: new UntypedFormControl(departureDate, [
          Validators.required,
          validateMulticityDeptDate(this.bookWidgetForm, n)
        ]),
        ['connectionAirport']: new UntypedFormControl(connection)
      },
      validateConnectionAndAirports(
        'airports.fromAirportcode',
        'airports.toAirportcode',
        'connectionAirport'
      )
    );
  }

  getFlights(form) {
    return form.get('flights').controls;
  }

  flightItemDestSet(selectedDestAirportObj) {
    const selToAirportCode = selectedDestAirportObj[0];
    const selToAirportCity = selectedDestAirportObj[1];
    const flightIndex = selectedDestAirportObj[2];
    const odPairRestriction = selectedDestAirportObj[3];
    const selToCountryCode = selectedDestAirportObj[7];
    if (this.rows) {
      const items: any[] = this.rows.toArray();
      if (flightIndex < this.rows.length - 1) {
        items[flightIndex + 1].fromAirportcode = selToAirportCode;
        items[flightIndex + 1].fromCountryCode = selToCountryCode;
        items[flightIndex + 1].fromCity = selToAirportCity;
        items[flightIndex + 1].cdf.detectChanges();
      }
      if (odPairRestriction.toString().toLowerCase() === 'true') {
        this.odPairRestriction = odPairRestriction;
        this.restrictions(
          flightIndex,
          selectedDestAirportObj[4],
          selectedDestAirportObj[5],
          selectedDestAirportObj[6]
        );
      }
    }
  }

  restrictions(index, tempRestrictMPM, tempRestrictMiles, tempRestrictCash) {
    const milesRestrictions = [];
    const mpmRestrictions = [];
    const cashRestrictions = [];
    const restrictions = [];
    restrictions[0] = false;
    restrictions[1] = false;
    restrictions[2] = false;
    for (let i = 0; i < this.flights.value.length; i++) {
      if (i === index) {
        mpmRestrictions.push(tempRestrictMPM);
        milesRestrictions.push(tempRestrictMiles);
        cashRestrictions.push(tempRestrictCash);
      } else {
        mpmRestrictions.push(this.flights.value[i].airports.mpmRestriction);
        milesRestrictions.push(this.flights.value[i].airports.milesRestriction);
        cashRestrictions.push(this.flights.value[i].airports.cashRestriction);
      }
    }
    for (let i = 0; i < this.flights.value.length; i++) {
      if (
        mpmRestrictions[i] !== undefined &&
        mpmRestrictions[i].toString().toUpperCase() === 'TRUE'
      ) {
        restrictions[0] = true;
      }
      if (
        milesRestrictions[i] !== undefined &&
        milesRestrictions[i].toString().toUpperCase() === 'TRUE'
      ) {
        restrictions[1] = true;
      }
      if (
        cashRestrictions[i] !== undefined &&
        cashRestrictions[i].toString().toUpperCase() === 'TRUE'
      ) {
        restrictions[2] = true;
      }
    }
    this.setODPairRestrictions.emit(restrictions);
  }

  fillDataForMulticity(recentSearch?) {
    let fromAirportCode = '';
    let fromCity = '';
    let toAirportcode = '';
    let toCity = '';
    let departDate = '';
    let fromCountryCode = '';
    let toCountryCode = '';

    if (recentSearch && recentSearch.airports && recentSearch.dates) {
      fromAirportCode = recentSearch.airports.fromAirportcode
        ? recentSearch.airports.fromAirportcode
        : '';
      fromCity = recentSearch.airports.fromCity
        ? recentSearch.airports.fromCity
        : '';
      toAirportcode = recentSearch.airports.toAirportcode
        ? recentSearch.airports.toAirportcode
        : '';
      toCity = recentSearch.airports.toCity ? recentSearch.airports.toCity : '';
      departDate = recentSearch.dates.departureDate
        ? recentSearch.dates.departureDate
        : '';
      fromCountryCode = recentSearch.airports?.fromCountryCode ?? '';
      toCountryCode = recentSearch.airports?.toCountryCode ?? '';
    } else {
      const airport = this.bookWidgetForm.get('airports') as UntypedFormGroup;
      if (airport) {
        fromAirportCode = airport.get('fromAirportcode')
          ? airport.get('fromAirportcode').value
          : '';
        fromCity = airport.get('fromCity') ? airport.get('fromCity').value : '';
        toAirportcode = airport.get('toAirportcode')
          ? airport.get('toAirportcode').value
          : '';
        toCity = airport.get('toCity') ? airport.get('toCity').value : '';
        departDate = this.bookWidgetForm.get('dates.departureDate')
          ? this.bookWidgetForm.get('dates.departureDate').value
          : '';
        fromCountryCode = airport.get('fromCountryCode')?.value ?? '';
        toCountryCode = airport.get('toCountryCode')?.value ?? '';
      }
    }

    this.flights = this.fb.array([
      this.createNewFlight(
        1,
        fromAirportCode,
        fromCity,
        toAirportcode,
        toCity,
        '',
        departDate,
        fromCountryCode,
        toCountryCode
      ),
      this.createNewFlight(
        2,
        toAirportcode,
        toCity,
        '',
        '',
        '',
        departDate,
        toCountryCode,
        ''
      )
    ]);
  }

  fillDataForOther() {
    // Create a searchSegmentData object
    const searchData: SearchSegmentData = {
      fromAirportCode: '',
      fromCity: '',
      toAirportcode: '',
      toCity: '',
      departDate: '',
      returnDate: '',
      fromCountryCode: '',
      toCountryCode: ''
    };

    if (this.flights && this.flights.length) {
      searchData.fromAirportCode = this.flights
        .at(0)
        .get('airports.fromAirportcode')
        ? this.flights.at(0).get('airports.fromAirportcode').value
        : '';
      searchData.fromCity = this.flights.at(0).get('airports.fromCity')
        ? this.flights.at(0).get('airports.fromCity').value
        : '';
      searchData.toAirportcode = this.flights
        .at(0)
        .get('airports.toAirportcode')
        ? this.flights.at(0).get('airports.toAirportcode').value
        : '';
      searchData.toCity = this.flights.at(0).get('airports.toCity')
        ? this.flights.at(0).get('airports.toCity').value
        : '';
      searchData.departDate = this.flights.at(0).get('departureDate')
        ? this.flights.at(0).get('departureDate').value
        : '';
      searchData.returnDate = '';
      searchData.fromCountryCode =
        this.flights.at(0).get('airports.fromCountryCode')?.value ?? '';
      searchData.toCountryCode =
        this.flights.at(0).get('airports.toCountryCode')?.value ?? '';
    }

    this.tripTypeChnagedTriggered.emit(searchData);
  }
}
